import { PropsWithChildren, FC, useEffect, useMemo } from 'react';

import { useApplication } from '@eneco/common/application';
import env from '@eneco/common/env';
import { Script, useRouter } from '@eneco/next';
import { useGTM } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { LayoutServiceExperimentInfo } from '@sitecore/types/lib';

// The noscript iframe is disabled (at least for now), since we can't login when JS is disabled anyway
// const GTM_NS_URL = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;

const GTM_PLATFORM_VERSION = 4;

export interface Props {
  customerId?: string;
  customerIdEncoded?: string;
  accountId?: number | null;
  experimentInfo?: LayoutServiceExperimentInfo;
}

const experimentInfoAsObject = (experimentInfo: LayoutServiceExperimentInfo) => {
  return {
    experimentId: experimentInfo.id,
    experimentName: experimentInfo.name,
    experimentVariant: experimentInfo.variant,
    experimentPath: experimentInfo.path,
  };
};

const GTMTag: FC<PropsWithChildren<Props & { gtmId: string }>> = ({
  customerId,
  customerIdEncoded,
  gtmId,
}) => {
  const { pushToDataLayer } = useGTM();
  const GTM_URL = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;

  const experimentInfoRaw = useContent().context?.experimentInfo;
  const experimentInfo = experimentInfoRaw && experimentInfoAsObject(experimentInfoRaw);

  const handleRouteChange = () => {
    if (experimentInfo) {
      pushToDataLayer({
        event: 'view_experiment',
        ...experimentInfo,
      });
    }
  };

  const router = useRouter();
  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  const customerIdentityEvent = useMemo(
    () => ({
      clientId: customerId,
      clientIdEncoded: customerIdEncoded,
      platformVersion: GTM_PLATFORM_VERSION,
    }),
    [customerId, customerIdEncoded],
  );

  const initialScripts = [
    `window.dataLayer = window.dataLayer || [];`,
    `window.dataLayer.push(${JSON.stringify(customerIdentityEvent)});`,
    `window.dataLayer.push({'gtm.start': ${new Date().getTime()}, event: 'gtm.js'});`,
  ];
  if (experimentInfo) {
    initialScripts.push(
      `window.dataLayer.push(${JSON.stringify({
        event: 'view_experiment',
        ...experimentInfo,
      })});`,
    );
  }

  return (
    <>
      <Script id="gtm-start" strategy="afterInteractive">
        {initialScripts.join('\n')}
      </Script>
      <Script strategy="afterInteractive" src={GTM_URL} id="gtm-script" />
    </>
  );
};

const GTMTagWrapper: FC<PropsWithChildren<Props>> = ({ ...args }: Props) => {
  const GTM_ID = env('GTM_ID');
  const { searchParams } = useApplication();
  if (!GTM_ID || searchParams.get('DisableGTM') === '1') return null;
  return <GTMTag {...args} gtmId={GTM_ID} />;
};

export default GTMTagWrapper;
